/**
 * 货架网格几何算法。
 *
 * 数学来源：`cuito-cashier-web/src/hooks/useSquareCellSize.ts:13-19`。
 * 新模型（`03-data-model.md` §4.4）把「固定 3 行等宽格子」改为
 * 「坐标底板 + 可合并矩形货架」，因此本模块在保留原数学的前提下新增三个能力：
 *   ① 行数可配（不再是硬编码 3）
 *   ② 44px 触控下限（REQ-NFR-025）——低于下限时不再缩小，改为横向滚动
 *   ③ `shelfRect` 把合并矩形换算为与网格线严格对齐的像素矩形
 */

// 几何判定（`ShelfRegion` / `regionsOverlap`）的唯一归属地是 `@cuito/types`：
// 服务端必须与客户端用**同款判定**（REQ-SHELF-004）。这里只做 re-export，
// 保持既有 import 路径（`from './shelfGrid'`）对 `ShelfGrid.tsx` 与
// `__tests__/transport.spec.tsx` 继续可用。
import { regionsOverlap, type ShelfRegion } from '@cuito/types';

export { regionsOverlap };
export type { ShelfRegion };

/** 网格间距，等于 Tailwind 的 `gap-2`。改动必须同步 `05-ui-design-system.md` §4.5 */
export const GAP = 8;
/** 触控目标下限（REQ-NFR-025） */
export const MIN_CELL = 44;
/** 格子边长上限，防止大屏上格子过大显得笨重 */
export const MAX_CELL = 128;

export interface GridMetrics {
  /** 实际使用的格子边长（已应用 44 下限与 128 上限） */
  cellSize: number;
  /**
   * 网格是否超出容器而需要滚动（= `overflowX || overflowY`）。
   *
   * 保留这个名字是为了不动既有调用点；新代码请按方向读 `overflowX` / `overflowY`。
   */
  scrollable: boolean;
  /** 内容宽超出容器宽 → 需要横向滚动 */
  overflowX: boolean;
  /** 内容高超出容器高 → 需要纵向滚动 */
  overflowY: boolean;
  /** 滚动内容总宽（cols 个格子 + 间隙） */
  contentWidth: number;
  /** 滚动内容总高（rows 个格子 + 间隙） */
  contentHeight: number;
}

/**
 * 容器尚未测量（尺寸为 0）或输入非法时的空度量。
 *
 * 返回新对象而不是共享常量：调用方（组件 state）不该因为别人改了这份对象而串味。
 */
function emptyMetrics(): GridMetrics {
  return {
    cellSize: 0,
    scrollable: false,
    overflowX: false,
    overflowY: false,
    contentWidth: 0,
    contentHeight: 0,
  };
}

export interface GridInput {
  containerWidth: number;
  containerHeight: number;
  rows: number;
  cols: number;
}

/**
 * 计算货架网格的格子边长与滚动需求。
 *
 * 容器尺寸为 0（首帧尚未测量）时返回 `cellSize: 0`，调用方据此不渲染网格，
 * 避免闪烁——这是基线 `shop/ShelfGrid.tsx:50` 的处理，必须保留。
 * `NaN` 也走这条路：`NaN <= 0` 是 false，不先挡掉就会把 `cellSize` / `contentWidth` /
 * `contentHeight` 一起算成 NaN 返回给调用方。今天的组件靠 `cellSize > 0` 恰好也挡住了
 * 渲染，但只要有人把 `contentWidth` 拿去写 style，NaN 会让浏览器丢掉整条声明、
 * 元素退化成「按内容撑开」——这种扩散必须在源头挡住。
 *
 * **两个方向都要报告**：`cellSize` 有 44px 触控下限（REQ-NFR-025），所以行数一多
 * 内容高就必然超过容器（例：1400×368 / 12 行 → cellSize 44、内容高 616）。
 * 旧实现只比较宽度，纵向溢出既没有信号也没有滚动能力，内容被外层
 * `overflow-hidden` 直接裁掉；因此这里同时给出 `overflowX` / `overflowY`，
 * 由渲染层决定在哪一轴开滚动条。
 */
export function computeGridMetrics(input: GridInput): GridMetrics {
  const { containerWidth: W, containerHeight: H, rows, cols } = input;
  if (!Number.isFinite(W) || !Number.isFinite(H)) {
    return emptyMetrics();
  }
  if (!Number.isFinite(rows) || !Number.isFinite(cols)) {
    return emptyMetrics();
  }
  if (W <= 0 || H <= 0 || rows <= 0 || cols <= 0) {
    return emptyMetrics();
  }

  const maxCellW = (W - (cols - 1) * GAP) / cols;
  const maxCellH = (H - (rows - 1) * GAP) / rows;
  const raw = Math.max(0, Math.floor(Math.min(maxCellW, maxCellH)));

  const cellSize = Math.max(MIN_CELL, Math.min(raw, MAX_CELL));
  const contentWidth = cols * cellSize + (cols - 1) * GAP;
  const contentHeight = rows * cellSize + (rows - 1) * GAP;

  const overflowX = contentWidth > W;
  const overflowY = contentHeight > H;

  return {
    cellSize,
    scrollable: overflowX || overflowY,
    overflowX,
    overflowY,
    contentWidth,
    contentHeight,
  };
}

export interface PixelRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * 把合并矩形换算为像素矩形。
 * 结果恒为整数、非负，且边界与网格线严格对齐（区域 = 若干格子 + 若干间隙）。
 *
 * **前置条件**：`region` 是 1-based 且 span ≥ 1。上游已经保证这一点——
 * 服务端 `create-shelf.dto.ts:15-29` / `update-shelf.dto.ts:52-69` 对四个字段都是
 * `@IsInt() @Min(1)`，合并选择器也只会产出合法区域。
 *
 * **为什么仍然夹取到 ≥ 0，而不是只写前置条件**：CSS 里负的 `width`/`height`
 * 是**非法声明**，浏览器会丢掉整条内联样式，元素退化成「按内容撑开」——
 * 一个 0 尺寸的隐形方块好排查，一个尺寸错乱还压在别的格子上的方块不好排查。
 * 代价：真出现退化输入时表现为「这个货架不显示」而不是报错；上游校验（DTO）
 * 仍是权威，这里的夹取只兜住渲染，不承担数据校验职责。
 */
export function shelfRect(region: ShelfRegion, cellSize: number): PixelRect {
  return {
    left: Math.max(0, (region.colStart - 1) * (cellSize + GAP)),
    top: Math.max(0, (region.rowStart - 1) * (cellSize + GAP)),
    width: Math.max(0, region.colSpan * cellSize + (region.colSpan - 1) * GAP),
    height: Math.max(0, region.rowSpan * cellSize + (region.rowSpan - 1) * GAP),
  };
}

/** 按行数生成默认行标签 A、B、C…（超过 26 行时退化为两位字母） */
export function defaultRowLabels(rows: number): string[] {
  const base = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return Array.from({ length: rows }, (_, i) =>
    i < 26 ? base[i]! : `${base[Math.floor(i / 26) - 1]}${base[i % 26]}`,
  );
}
